/**
 * ユーザー管理APIクライアント
 */

import { AuthenticatedApiClient } from '../client';
import type {
  AppDbUserSettings,
  BulkCreateUsersRequest,
  BulkCreateUsersResponse,
  BulkValidateUsersRequest,
  BulkValidateUsersResponse,
  CompanyUser,
  CreateUserRequest,
  CreateUserResponse,
  EnableAllHubAccessResponse,
  ResetPasswordResponse,
  UpdateUserRequest,
} from '@/lib/types/mise/user';

/**
 * ユーザーを作成（認証付き）
 */
export async function createUserAuthenticated(
  companyId: string,
  request: CreateUserRequest,
  authHeaders: Record<string, string>,
): Promise<CreateUserResponse> {
  try {
    const client = new AuthenticatedApiClient(authHeaders);
    const user = await client.post<CreateUserResponse>(
      `/mise/companies/${companyId}/users`,
      request,
    );
    return user;
  } catch (error) {
    console.error(`Failed to create user for company ${companyId}:`, error);
    throw error;
  }
}

/**
 * ユーザーを更新（認証付き）
 */
export async function updateUserAuthenticated(
  companyId: string,
  userId: string,
  request: UpdateUserRequest,
  authHeaders: Record<string, string>,
): Promise<CompanyUser> {
  try {
    const client = new AuthenticatedApiClient(authHeaders);
    const user = await client.put<CompanyUser>(
      `/mise/companies/${companyId}/users/${userId}`,
      request,
    );
    return user;
  } catch (error) {
    console.error(`Failed to update user ${userId} for company ${companyId}:`, error);
    throw error;
  }
}

/**
 * appdbのユーザー設定を取得（認証付き）
 */
export async function getAppDbUserSettingsAuthenticated(
  companyId: string,
  userId: string,
  accountId: string,
  authHeaders: Record<string, string>,
): Promise<AppDbUserSettings> {
  try {
    const client = new AuthenticatedApiClient(authHeaders);
    return await client.get<AppDbUserSettings>(
      `/mise/companies/${companyId}/users/${userId}/appdb-settings?account_id=${encodeURIComponent(accountId)}`,
    );
  } catch (error) {
    console.error(
      `Failed to get appdb user settings for user ${userId}, account ${accountId}:`,
      error,
    );
    throw error;
  }
}

/**
 * パスワードを再発行（認証付き）
 */
export async function resetPasswordAuthenticated(
  companyId: string,
  userId: string,
  authHeaders: Record<string, string>,
): Promise<ResetPasswordResponse> {
  try {
    const client = new AuthenticatedApiClient(authHeaders);
    return await client.post<ResetPasswordResponse>(
      `/mise/companies/${companyId}/users/${userId}/reset-password`,
    );
  } catch (error) {
    console.error(`Failed to reset password for user ${userId} in company ${companyId}:`, error);
    throw error;
  }
}

/**
 * ユーザーを削除（認証付き・バックエンドの allow_force_delete=true の環境で動作）
 */
export async function deleteUserAuthenticated(
  companyId: string,
  userId: string,
  authHeaders: Record<string, string>,
): Promise<void> {
  try {
    const client = new AuthenticatedApiClient(authHeaders);
    await client.delete(`/mise/companies/${companyId}/users/${userId}`);
  } catch (error) {
    console.error(`Failed to delete user ${userId} for company ${companyId}:`, error);
    throw error;
  }
}

/**
 * ユーザー一括登録のバリデーション(認証付き)
 */
export async function bulkValidateUsersAuthenticated(
  companyId: string,
  request: BulkValidateUsersRequest,
  authHeaders: Record<string, string>,
): Promise<BulkValidateUsersResponse> {
  try {
    const client = new AuthenticatedApiClient(authHeaders);
    return await client.post<BulkValidateUsersResponse>(
      `/mise/companies/${companyId}/users/bulk-validate`,
      request,
    );
  } catch (error) {
    console.error(`Failed to validate bulk users for company ${companyId}:`, error);
    throw error;
  }
}

/**
 * ユーザー一括登録の実行(認証付き)
 */
export async function bulkCreateUsersAuthenticated(
  companyId: string,
  request: BulkCreateUsersRequest,
  authHeaders: Record<string, string>,
): Promise<BulkCreateUsersResponse> {
  try {
    const client = new AuthenticatedApiClient(authHeaders);
    return await client.post<BulkCreateUsersResponse>(
      `/mise/companies/${companyId}/users/bulk-create`,
      request,
    );
  } catch (error) {
    console.error(`Failed to bulk-create users for company ${companyId}:`, error);
    throw error;
  }
}

/**
 * 指定ユーザーの StoreHub アクセスを ON/OFF 切替（認証付き）
 */
export async function toggleHubAccessAuthenticated(
  companyId: string,
  email: string,
  accountId: string,
  enabled: boolean,
  authHeaders: Record<string, string>,
): Promise<void> {
  try {
    const client = new AuthenticatedApiClient(authHeaders);
    await client.patch(
      `/mise/companies/${companyId}/users/${encodeURIComponent(email)}/hub-access`,
      { account_id: accountId, enabled },
    );
  } catch (error) {
    console.error(
      `Failed to toggle hub access for ${email} (account=${accountId}) in company ${companyId}:`,
      error,
    );
    throw error;
  }
}

/**
 * 企業の全ユーザーの StoreHub アクセスを、各ユーザーがアクセスできる全アカウントで ON にする（認証付き）
 */
export async function enableAllHubAccessAuthenticated(
  companyId: string,
  authHeaders: Record<string, string>,
): Promise<EnableAllHubAccessResponse> {
  try {
    const client = new AuthenticatedApiClient(authHeaders);
    return await client.post<EnableAllHubAccessResponse>(
      `/mise/companies/${companyId}/hub-access/enable-all`,
    );
  } catch (error) {
    console.error(`Failed to enable hub access for all users in company ${companyId}:`, error);
    throw error;
  }
}
